import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowDownTrayIcon, PencilIcon, PrinterIcon } from '@heroicons/react/24/outline';
import Layout from '../../components/Layout';
import { truckingBillAPI } from '../../api/waybillAPI';
import { exportToCSV } from '../../utils/csvExport';
import { useAuth } from '../../context/AuthContext';
import { formatPHP as PHP, formatDatePH as fmt } from '../../utils/formatters';

function StatusBadge({ status }) {
  const map = {
    draft:   'bg-gray-100 text-gray-600',
    sent:    'bg-blue-100 text-blue-700',
    paid:           'bg-green-100 text-green-700',
    partially_paid: 'bg-yellow-100 text-yellow-700',
    overdue:        'bg-red-100 text-red-700',
    voided:         'bg-red-100 text-red-600 line-through',
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${map[status] || map.draft}`}>
      {status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Draft'}
    </span>
  );
}

const daysOverdue = (b) => {
  if (b.status !== 'overdue') return null;
  const diff = Math.floor((Date.now() - new Date(b.BillDueDate)) / 86400000);
  return diff > 0 ? diff : null;
};

function aging(bills) {
  const buckets = { current: [], d30: [], d60: [], d90: [], d90plus: [] };
  bills.forEach(b => {
    if (b.status !== 'overdue') { buckets.current.push(b); return; }
    const days = Math.floor((Date.now() - new Date(b.BillDueDate)) / 86400000);
    if (days <= 30)       buckets.d30.push(b);
    else if (days <= 60)  buckets.d60.push(b);
    else if (days <= 90)  buckets.d90.push(b);
    else                  buckets.d90plus.push(b);
  });
  return buckets;
}

function bucketTotal(arr) {
  return arr.reduce((s, b) => s + (b.amountDue || 0), 0);
}

const customerNameOf = (bill) => (
  bill.WayBillId?.CustomerId?.CustomerName
  || bill.WayBillId?.CustomerId?.name
  || '—'
);

export default function ARPage() {
  const navigate = useNavigate();
  const { selectedClient } = useAuth();
  const accountingClientId = selectedClient?._id || localStorage.getItem('accountingClientId') || null;

  const [data, setData] = useState({ bills: [], summary: {} });
  const [filters, setFilters] = useState({ dateFrom: '', dateTo: '', status: '', customer: '' });
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchAR(); }, [accountingClientId]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchAR = async (currentFilters = filters) => {
    setLoading(true);
    try {
      const params = { ...currentFilters };
      if (accountingClientId) params.accountingClient = accountingClientId;
      const res = await truckingBillAPI.getARSummary(params);
      setData(res.data?.data || { bills: [], summary: {} });
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const setF = (k) => (e) => setFilters(p => ({ ...p, [k]: e.target.value }));

  const bills = data.bills || [];

  // Client-side customer filter
  const displayed = filters.customer
    ? bills.filter(b => {
        const cn = (
          b.WayBillId?.CustomerId?.CustomerName ||
          b.WayBillId?.CustomerId?.name || ''
        ).toLowerCase();
        return cn.includes(filters.customer.toLowerCase());
      })
    : bills;

  const buckets = aging(displayed);

  const filteredSummary = {
    totalAR: displayed.reduce((sum, b) => sum + (b.amountDue || 0), 0),
    overdueAR: displayed
      .filter((b) => b.status === 'overdue')
      .reduce((sum, b) => sum + (b.amountDue || 0), 0),
    currentAR: displayed
      .filter((b) => b.status !== 'overdue')
      .reduce((sum, b) => sum + (b.amountDue || 0), 0),
    count: displayed.length,
  };

  const handlePrintReport = () => {
    const params = new URLSearchParams({
      dateFrom: filters.dateFrom,
      dateTo: filters.dateTo,
      status: filters.status,
      customer: filters.customer,
    });
    window.open(`/billing/reports/print/ar?${params}`, '_blank');
  };

  const handleExportCSV = () => {
    const rows = displayed.map((b) => ({
      'Bill No.': b.BillName || '—',
      'Waybill Serial': b.WayBillId?.SerialNo || '—',
      Customer: customerNameOf(b),
      'Bill Date': fmt(b.BillDate),
      'Due Date': fmt(b.BillDueDate),
      'Amount Due': Number(b.amountDue || 0).toFixed(2),
      'Days Overdue': daysOverdue(b) || '',
      Status: b.status || 'draft',
    }));

    const from = filters.dateFrom || 'all';
    const to = filters.dateTo || 'all';
    exportToCSV(rows, `ar-report-${from}-to-${to}.csv`);
  };

  return (
    <Layout pageTitle="Accounts Receivable">
      <div className="space-y-6">
        {/* Summary cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Total AR</p>
            <p className="text-2xl font-semibold text-teal-700">₱{PHP(filteredSummary.totalAR)}</p>
            <p className="text-xs text-gray-400 mt-1">{filteredSummary.count} invoice(s)</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Overdue</p>
            <p className="text-2xl font-semibold text-red-600">₱{PHP(filteredSummary.overdueAR)}</p>
            <p className="text-xs text-gray-400 mt-1">{displayed.filter(b => b.status === 'overdue').length} invoice(s)</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Current (not due)</p>
            <p className="text-2xl font-semibold text-blue-600">₱{PHP(filteredSummary.currentAR)}</p>
            <p className="text-xs text-gray-400 mt-1">{displayed.filter(b => b.status !== 'overdue').length} invoice(s)</p>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow p-4 flex flex-wrap items-end gap-3 no-print">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Date From</label>
            <input type="date" value={filters.dateFrom} onChange={setF('dateFrom')}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Date To</label>
            <input type="date" value={filters.dateTo} onChange={setF('dateTo')}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Status</label>
            <select value={filters.status} onChange={setF('status')}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500">
              <option value="">All</option>
              <option value="sent">Sent</option>
              <option value="overdue">Overdue</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Customer</label>
            <input type="text" value={filters.customer} onChange={setF('customer')} placeholder="Search customer…"
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500" />
          </div>
          <button onClick={() => fetchAR(filters)}
            className="px-4 py-2 bg-teal-600 text-white text-sm rounded-lg hover:bg-teal-700">
            Apply
          </button>
          <button onClick={() => {
            const cleared = { dateFrom: '', dateTo: '', status: '', customer: '' };
            setFilters(cleared);
            fetchAR(cleared);
          }}
            className="px-4 py-2 border border-gray-300 text-gray-600 text-sm rounded-lg hover:bg-gray-50">
            Clear
          </button>
          <button onClick={handlePrintReport}
            className="px-4 py-2 border border-gray-300 text-gray-700 text-sm rounded-lg hover:bg-gray-50 inline-flex items-center gap-2">
            <PrinterIcon className="h-4 w-4" />
            Print Report
          </button>
          <button onClick={handleExportCSV}
            className="px-4 py-2 bg-emerald-600 text-white text-sm rounded-lg hover:bg-emerald-700 inline-flex items-center gap-2">
            <ArrowDownTrayIcon className="h-4 w-4" />
            Export CSV
          </button>
        </div>

        {/* AR Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                {['Bill No.', 'Waybill Serial', 'Customer', 'Bill Date', 'Due Date', 'Amount Due', 'Days Overdue', 'Status', 'Actions'].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                Array.from({ length: 4 }).map((_, i) => (
                  <tr key={i}>{Array.from({ length: 9 }).map((_, j) => (
                    <td key={j} className="px-4 py-3"><div className="h-4 bg-gray-200 rounded animate-pulse" /></td>
                  ))}</tr>
                ))
              ) : displayed.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-4 py-10 text-center text-gray-400">No AR records found.</td>
                </tr>
              ) : displayed.map(b => {
                const overdueDays = daysOverdue(b);
                const customerName = customerNameOf(b);
                return (
                  <tr key={b._id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-gray-900">{b.BillName}</td>
                    <td className="px-4 py-3 text-gray-600">{b.WayBillId?.SerialNo || '—'}</td>
                    <td className="px-4 py-3 text-gray-600">{customerName}</td>
                    <td className="px-4 py-3 text-gray-600">{fmt(b.BillDate)}</td>
                    <td className="px-4 py-3 text-gray-600">{fmt(b.BillDueDate)}</td>
                    <td className="px-4 py-3 font-medium text-gray-900">₱{PHP(b.amountDue)}</td>
                    <td className="px-4 py-3">
                      {overdueDays ? <span className="text-red-600 font-semibold">{overdueDays}d</span> : <span className="text-gray-400">—</span>}
                    </td>
                    <td className="px-4 py-3"><StatusBadge status={b.status} /></td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button onClick={() => navigate(`/billing/ff-billing/${b._id}/edit`)}
                          className="p-1.5 rounded hover:bg-teal-50 text-teal-600" title="Edit">
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button onClick={() => window.open(`/billing/ff-billing/${b._id}/print`, '_blank')}
                          className="p-1.5 rounded hover:bg-gray-50 text-gray-500" title="Print">
                          <PrinterIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Aging Buckets */}
        {!loading && displayed.length > 0 && (
          <div className="bg-white rounded-lg shadow p-5">
            <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">Overdue Aging Summary</h3>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-2 text-gray-500 font-medium">Bucket</th>
                  <th className="text-right py-2 text-gray-500 font-medium">Count</th>
                  <th className="text-right py-2 text-gray-500 font-medium">Total</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { label: 'Current (not yet due)', key: 'current' },
                  { label: '1–30 days overdue',     key: 'd30' },
                  { label: '31–60 days overdue',    key: 'd60' },
                  { label: '61–90 days overdue',    key: 'd90' },
                  { label: '90+ days overdue',      key: 'd90plus' },
                ].map(({ label, key }) => (
                  <tr key={key} className="border-b border-gray-100">
                    <td className="py-2 text-gray-700">{label}</td>
                    <td className="py-2 text-right text-gray-600">{buckets[key].length}</td>
                    <td className="py-2 text-right font-medium text-gray-900">₱{PHP(bucketTotal(buckets[key]))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Layout>
  );
}
